import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiGet, apiPost, apiPut, apiUploadFile, getMySignature, setMySignature, getErrorMessage } from '../../utils/api'
import { toast } from '../shared/NotificationToast'
import { RichTextEditor } from '../shared/RichTextEditor'

interface ActivityChat {
  id: number | string
  status: string
  created_at: string
  channel?: string
  visitor_name?: string | null
  company_name?: string | null
  resolved?: boolean
}
interface ActivityResponse {
  stats: { chats_handled: number; messages_sent: number; resolved: number; last_active_at: string | null }
  chats: ActivityChat[]
}

function fmtDateTime(s?: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-xl border border-border p-4 bg-surface">
    <p className="text-[11px] text-txt-secondary uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold text-txt-primary mt-1">{value}</p>
  </div>
)

export const ProfilePage: React.FC = () => {
  const { currentAgent, updateAgent } = useAuth()
  const navigate = useNavigate()
  const fileRef = useRef<HTMLInputElement>(null)

  const [avatarUrl, setAvatarUrl] = useState<string | null>(currentAgent?.avatar_url || null)
  const [name, setName] = useState(currentAgent?.name || '')
  const [savingName, setSavingName] = useState(false)
  const [uploading, setUploading] = useState(false)

  const [curPw, setCurPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [changingPw, setChangingPw] = useState(false)

  const [activity, setActivity] = useState<ActivityResponse | null>(null)

  // Ticket-reply signature (HTML). The server appends this to outgoing replies.
  const [signature, setSignature] = useState('')
  const [savedSignature, setSavedSignature] = useState('')
  const [signatureLoaded, setSignatureLoaded] = useState(false)
  const [savingSignature, setSavingSignature] = useState(false)

  // Pull the canonical agent record (avatar/name) + activity on mount.
  useEffect(() => {
    if (!currentAgent?.id) return
    let active = true
    apiGet<any>('/agents').then((res) => {
      if (!active) return
      const list = Array.isArray(res) ? res : (res.agents || res.data || [])
      const me = list.find((a: any) => String(a.id) === String(currentAgent.id))
      if (me) { setAvatarUrl(me.avatar_url || null); setName(me.name || currentAgent.name) }
    }).catch(() => {})
    apiGet<ActivityResponse>(`/agents/${currentAgent.id}/activity`).then((a) => { if (active) setActivity(a) }).catch(() => {})
    getMySignature().then((res) => {
      if (!active) return
      const sig = res.signature || ''
      setSignature(sig)
      setSavedSignature(sig)
      setSignatureLoaded(true)
    }).catch(() => { if (active) setSignatureLoaded(true) })
    return () => { active = false }
  }, [currentAgent?.id])

  if (!currentAgent) return null

  const onPickAvatar = () => fileRef.current?.click()
  const onAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    try {
      const res = await apiUploadFile<any>(`/agents/${currentAgent.id}/avatar`, file)
      const url = res.avatar_url || res.url
      setAvatarUrl(url)
      updateAgent({ avatar_url: url })
      toast.success('Profile picture updated')
    } catch (err) {
      toast.error('Could not update picture', getErrorMessage(err, 'Please try a different image'))
    } finally {
      setUploading(false)
    }
  }

  const saveName = async () => {
    const trimmed = name.trim()
    if (!trimmed || trimmed === currentAgent.name) return
    setSavingName(true)
    try {
      await apiPut(`/agents/${currentAgent.id}`, { name: trimmed })
      updateAgent({ name: trimmed })
      toast.success('Name updated')
    } catch (err) {
      toast.error('Could not update name', getErrorMessage(err))
    } finally {
      setSavingName(false)
    }
  }

  const changePassword = async () => {
    if (!curPw || !newPw) { toast.error('Fill in all password fields'); return }
    if (newPw.length < 8) { toast.error('New password must be at least 8 characters'); return }
    if (newPw !== confirmPw) { toast.error('New passwords do not match'); return }
    setChangingPw(true)
    try {
      await apiPost('/agents/change-password', { current_password: curPw, new_password: newPw })
      setCurPw(''); setNewPw(''); setConfirmPw('')
      toast.success('Password changed')
    } catch (err) {
      toast.error('Could not change password', getErrorMessage(err, 'Check your current password'))
    } finally {
      setChangingPw(false)
    }
  }

  const saveSignature = async () => {
    setSavingSignature(true)
    try {
      await setMySignature(signature)
      setSavedSignature(signature)
      toast.success(signature.trim() ? 'Signature saved' : 'Signature cleared')
    } catch (err) {
      toast.error('Could not save signature', getErrorMessage(err))
    } finally {
      setSavingSignature(false)
    }
  }

  const clearSignature = async () => {
    setSavingSignature(true)
    try {
      await setMySignature('')
      setSignature('')
      setSavedSignature('')
      toast.success('Signature cleared')
    } catch (err) {
      toast.error('Could not clear signature', getErrorMessage(err))
    } finally {
      setSavingSignature(false)
    }
  }

  const signatureDirty = signature.trim() !== savedSignature.trim()
  const hasSavedSignature = savedSignature.trim().length > 0

  const initials = (currentAgent.name || 'A').trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join('')
  const isOpen = (s: string) => ['queued', 'open', 'assigned', 'missed', 'reopened'].includes(s)

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
      <h1 className="text-xl font-bold text-txt-primary">My Profile</h1>

      {/* Profile card */}
      <section className="rounded-xl border border-border bg-surface p-6">
        <div className="flex items-center gap-5">
          <button onClick={onPickAvatar} disabled={uploading} className="relative group shrink-0" title="Change picture">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="w-20 h-20 rounded-full object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-brand-600 text-white flex items-center justify-center text-2xl font-bold">{initials}</div>
            )}
            <span className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-[11px] font-medium transition-opacity">
              {uploading ? '…' : 'Change'}
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatarChange} />
          <div className="min-w-0">
            <p className="text-lg font-semibold text-txt-primary">{currentAgent.name}</p>
            <p className="text-sm text-txt-secondary">{currentAgent.email}</p>
            <span className="inline-block mt-1.5 text-[10px] font-semibold px-1.5 py-px rounded bg-brand-100 text-brand-700 uppercase">{currentAgent.role}</span>
          </div>
        </div>

        {/* Edit name */}
        <div className="mt-6 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <label className="block">
            <span className="text-xs font-medium text-txt-secondary">Display name</span>
            <input value={name} onChange={e => setName(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-border bg-surface text-sm text-txt-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          </label>
          <button onClick={saveName} disabled={savingName || !name.trim() || name.trim() === currentAgent.name} className="px-4 py-2 rounded-lg text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40 transition-colors">{savingName ? 'Saving…' : 'Save'}</button>
        </div>
      </section>

      {/* Change password */}
      <section className="rounded-xl border border-border bg-surface p-6">
        <h2 className="text-sm font-semibold text-txt-primary mb-4">Change password</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <input type="password" placeholder="Current password" value={curPw} onChange={e => setCurPw(e.target.value)} autoComplete="current-password" className="px-3 py-2 rounded-lg border border-border bg-surface text-sm text-txt-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          <input type="password" placeholder="New password" value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" className="px-3 py-2 rounded-lg border border-border bg-surface text-sm text-txt-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          <input type="password" placeholder="Confirm new password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} autoComplete="new-password" className="px-3 py-2 rounded-lg border border-border bg-surface text-sm text-txt-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        </div>
        <button onClick={changePassword} disabled={changingPw} className="mt-4 px-4 py-2 rounded-lg text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40 transition-colors">{changingPw ? 'Updating…' : 'Update password'}</button>
      </section>

      {/* Ticket reply signature */}
      <section className="rounded-xl border border-border bg-surface p-6">
        <h2 className="text-sm font-semibold text-txt-primary">Ticket reply signature</h2>
        <p className="text-xs text-txt-secondary mt-1 mb-4">
          Added automatically to the end of every email reply you send from the ticket inbox.
          You don't need to type it into individual replies.
        </p>
        {signatureLoaded ? (
          <>
            <RichTextEditor
              value={signature}
              onChange={setSignature}
              disabled={savingSignature}
              placeholder="e.g. Best regards, Jane Doe · WeQuote Support"
              ariaLabel="Ticket reply signature"
              minHeight={88}
            />
            <div className="flex items-center justify-end gap-2 mt-3">
              {hasSavedSignature && (
                <button
                  onClick={clearSignature}
                  disabled={savingSignature}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-txt-secondary border border-border hover:bg-surface-hover disabled:opacity-40 transition-colors"
                >
                  Clear
                </button>
              )}
              <button
                onClick={saveSignature}
                disabled={savingSignature || !signatureDirty}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40 transition-colors"
              >
                {savingSignature ? 'Saving…' : 'Save signature'}
              </button>
            </div>
          </>
        ) : (
          <p className="text-sm text-txt-tertiary">Loading…</p>
        )}
      </section>

      {/* Activity */}
      <section>
        <h2 className="text-sm font-semibold text-txt-primary mb-3">My activity</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Stat label="Chats handled" value={activity?.stats.chats_handled ?? '—'} />
          <Stat label="Messages sent" value={activity?.stats.messages_sent ?? '—'} />
          <Stat label="Resolved" value={activity?.stats.resolved ?? '—'} />
          <Stat label="Last active" value={<span className="text-sm">{fmtDateTime(activity?.stats.last_active_at)}</span>} />
        </div>

        <div className="rounded-xl border border-border bg-surface overflow-hidden">
          <p className="px-5 py-3 border-b border-border text-[11px] font-semibold text-txt-secondary uppercase tracking-wider">Chats you've responded to</p>
          <div className="divide-y divide-border max-h-[28rem] overflow-y-auto">
            {activity?.chats?.length ? activity.chats.map(c => (
              <button key={c.id} onClick={() => navigate(`/queue/${c.id}`)} className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-surface-secondary transition-colors text-left group">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-txt-primary truncate">
                    Chat #{c.id}{c.visitor_name ? ` · ${c.visitor_name}` : ''}{c.company_name ? ` · ${c.company_name}` : ''}
                  </p>
                  <p className="text-xs text-txt-tertiary">{fmtDateTime(c.created_at)}</p>
                </div>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-medium px-1.5 py-px rounded capitalize ${c.resolved ? 'bg-green-100 text-green-700' : isOpen(c.status) ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{c.resolved ? 'Resolved' : (c.status || '—')}</span>
                  <svg className="w-4 h-4 text-txt-tertiary group-hover:text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </span>
              </button>
            )) : (
              <p className="px-5 py-8 text-sm text-txt-secondary text-center">{activity ? 'No chats yet.' : 'Loading…'}</p>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
